import { asArray, isDateGreater, isRealDate, round2, sumOf } from './promoPrimitives';
import { getOffer, refOfOfferInfo } from './promoModel';
import { totalFree, totalPaying } from './promoUnits';

// Offer-level checks of the local ("OP locale" / pirate) offers (#23521), shared by the status bars, the validation of
// the step and the order eligibility. Ports of visitStep3-promo.js:1070-1152.

/** `hasValidOfferDates` (JS:1095) : both dates set, the end strictly after the start. */
export function hasValidOfferDates(offerInfo) {
	if (!offerInfo) return true;
	return isRealDate(offerInfo.startDate) && isRealDate(offerInfo.endDate) && isDateGreater(offerInfo.endDate, offerInfo.startDate);
}

const isUbMode = (offer) => offer?.displayUnit === 1;

/**
 * Paying total of a product inside a local offer, as `isValid` / `getLocaleOfferProductUCError` sum it : every row of the
 * product keyed on the offer and not free, additional presences included (unlike the national check).
 */
export function localProductPaying(offerInfo, productId, ubMode, offerKey = offerInfo?._key) {
	const rows = asArray(offerInfo?._negociatedCampaignOffers).filter((row) => row.campaignId === offerInfo.campaignId && row.offerKey === offerKey && row.productId === productId && row.isFree === false);
	return sumOf(rows, ubMode ? 'volume' : 'volumeUC');
}

/** `getLocaleOfferProductUCError` (JS:1070) : paying volume present but outside the product min/max. */
export function localProductUCError(campaign, offerInfo, productId) {
	if (!offerInfo) return false;
	const offer = getOffer(campaign, offerInfo.campaignOfferId);
	const product = asArray(offer?.products).find((p) => p.productId === productId);
	if (!product) return false;
	const total = localProductPaying(offerInfo, productId, isUbMode(offer));
	if (total === 0) return false;
	return !!((product.minUC && total < product.minUC) || (product.maxUC && total > product.maxUC));
}

/** Max free quantity of a gratuity : `round2(paying × pctGratuity / 100)`, pctGratuity being a percentage (10 = 10%). */
export const maxFreeFor = (payingQty, pctGratuity) => round2((payingQty * pctGratuity) / 100);

/** Paying quantity a free row is measured against : the product's main paying row, in the offer unit. */
export function payingQtyFor(rows, freeRow, offer, ref = null) {
	const paying = asArray(rows).find((row) => row.productId === freeRow.productId && (!ref || (row.campaignId === ref.campaignId && row.campaignOfferId === ref.campaignOfferId)) && !row.isFree && !row.additional);
	if (!paying) return 0;
	return isUbMode(offer) ? paying.volume : paying.volumeUC;
}

/** `getLocaleOfferHasBlockingError` (JS:1101) : dates, product min/max, max free quantity, gratuity %, offer min/max. */
export function localOfferHasBlockingError(campaign, offerInfo) {
	if (!offerInfo) return false;
	const offer = getOffer(campaign, offerInfo.campaignOfferId);
	if (!offer) return false;
	if (!hasValidOfferDates(offerInfo)) return true;

	const rows = asArray(offerInfo._negociatedCampaignOffers);
	const ref = refOfOfferInfo(offerInfo);
	const ubMode = isUbMode(offer);

	if (rows.filter((row) => row.additional === false && row.isFree === false).some((row) => localProductUCError(campaign, offerInfo, row.productId))) return true;

	if (offer.maxFreeQty > 0 && totalFree(rows, ref, ubMode) > offer.maxFreeQty) return true;

	const freeRows = rows.filter((row) => row.additional === false && row.isFree === true);
	const gratuityExceeded = freeRows.some((freeRow) => {
		const offerProduct = asArray(offer.products).find((p) => p.productId === freeRow.productId);
		if (!offerProduct || !offerProduct.pctGratuity) return false;
		const freeQty = ubMode ? freeRow.volume : freeRow.volumeUC;
		return freeQty > maxFreeFor(payingQtyFor(rows, freeRow, offer), offerProduct.pctGratuity);
	});
	if (gratuityExceeded) return true;

	const paying = totalPaying(rows, ref, ubMode);
	const minOk = !offer.minUC || paying >= offer.minUC;
	const maxOk = !offer.maxUC || paying <= offer.maxUC;
	return !minOk || !maxOk;
}
